import AddIcon from "@mui/icons-material/AddRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import {
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { allInboxChannelTypes } from "../../../../api/types/inboxesTypes";
import useClientContext from "../../../../context/useClientContext";
import SparklesIcon from "@entrilia/shared/icons/SparklesIcon";
import { inboxChannelTypeCaptionsMap } from "../../../../utilities/enumCaptions";
import AgentInboxChannelTypeAvatar from "./AgentInboxChannelTypeAvatar";
import AgentInboxSettingsContext from "./AgentInboxSettingsContext";
import {
  addChannelDomainAction,
  changeAgentTypeAction,
  changeChannelAddressAction,
  changeChannelDescriptionAction,
  changeChannelDomainAction,
  changeChannelNameAction,
  changeChannelTypeAction,
  removeChannelDomainAction,
} from "./editInboxChannelState";

const AgentInboxChannelContainer = () => {
  const { clientCode } = useClientContext();

  const { editedChannelState, setEditedChannelState, editedChannelValidationResults, inbox, agents } =
    useDefinedContext(AgentInboxSettingsContext);

  if (editedChannelState === undefined) {
    return null;
  }

  return (
    <Stack spacing={3} sx={{ pt: 2.5 }}>
      <Typography variant="subtitle2">General</Typography>
      <TextField
        fullWidth
        label="Name"
        value={editedChannelState.channelForm.name}
        onChange={(e) => setEditedChannelState(changeChannelNameAction(e.target.value))}
        error={!editedChannelValidationResults?.name.isValid && editedChannelState.touchedFields.includes("name")}
        helperText={
          editedChannelState.touchedFields.includes("name") ? editedChannelValidationResults?.name.error : undefined
        }
      />
      <TextField
        fullWidth
        label="Description"
        value={editedChannelState.channelForm.description}
        onChange={(e) => setEditedChannelState(changeChannelDescriptionAction(e.target.value))}
        error={
          !editedChannelValidationResults?.description.isValid &&
          editedChannelState.touchedFields.includes("description")
        }
        helperText={
          editedChannelState.touchedFields.includes("description")
            ? editedChannelValidationResults?.description.error
            : undefined
        }
      />
      <FormControl sx={{ width: "50%" }}>
        <InputLabel>Channel Type</InputLabel>
        <Select
          label="Channel Type"
          value={editedChannelState.channelForm.type}
          onChange={(e) => setEditedChannelState(changeChannelTypeAction(e.target.value))}
          renderValue={(value) => (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <AgentInboxChannelTypeAvatar channelType={value} size={24} />
              <Typography>{inboxChannelTypeCaptionsMap[value]}</Typography>
            </Stack>
          )}
        >
          {allInboxChannelTypes.map((type) => (
            <MenuItem key={type} value={type}>
              <AgentInboxChannelTypeAvatar
                channelType={type}
                size={24}
                color={type === editedChannelState.channelForm.type ? "primary" : "secondary"}
              />
              <ListItemText primary={inboxChannelTypeCaptionsMap[type]} sx={{ pl: 1 }} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {editedChannelState.channelForm.type === "Email" && (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <TextField
            sx={{ width: "50%" }}
            label="Address"
            value={editedChannelState.channelForm.addressField}
            onChange={(e) => setEditedChannelState(changeChannelAddressAction(e.target.value))}
            error={
              !editedChannelValidationResults?.address.isValid && editedChannelState.touchedFields.includes("address")
            }
            helperText={
              editedChannelState.touchedFields.includes("address")
                ? editedChannelValidationResults?.address.error
                : undefined
            }
            slotProps={{
              input: {
                startAdornment: <Typography color="textDisabled">{clientCode}-</Typography>,
              },
            }}
          />
          <Typography color="textSecondary">@{inbox.addressDomain}</Typography>
        </Stack>
      )}
      {editedChannelState.channelForm.type === "Email" && (
        <Stack spacing={1}>
          <Typography variant="subtitle2">Whitelisted Domains</Typography>
          <Typography color="textSecondary">
            When set, incoming emails from other domains will be ignored by this channel.
          </Typography>
          {editedChannelState.channelForm.incomingDomainWhitelist.map(({ id, domain }, index) => (
            <Stack key={id} direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <TextField
                sx={{ width: "50%" }}
                value={domain}
                onChange={(e) => setEditedChannelState(changeChannelDomainAction(e.target.value, index))}
                error={!editedChannelValidationResults?.domainItems[index]?.isValid}
                helperText={editedChannelValidationResults?.domainItems[index]?.error}
              />
              <IconButton onClick={() => setEditedChannelState(removeChannelDomainAction(index))}>
                <CloseIcon color="error" />
              </IconButton>
            </Stack>
          ))}
          <Button
            variant="text"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setEditedChannelState(addChannelDomainAction())}
            sx={{ alignSelf: "flex-start" }}
          >
            Add
          </Button>
        </Stack>
      )}
      <Stack spacing={1}>
        <Typography variant="subtitle2">Agent</Typography>
        <Typography color="textSecondary">Select an agent that will be assigned to this channel.</Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Select
            sx={{ width: "50%" }}
            value={editedChannelState.channelForm.agentType}
            onChange={(e) => setEditedChannelState(changeAgentTypeAction(e.target.value))}
            startAdornment={
              <InputAdornment position="start">
                <SparklesIcon color="primary" />
              </InputAdornment>
            }
            error={
              !editedChannelValidationResults?.agentType.isValid &&
              editedChannelState.touchedFields.includes("agentType")
            }
          >
            {agents.map(({ workflowAgentType: agentType, agentName }) => (
              <MenuItem key={agentType} value={agentType}>
                <ListItemText primary={agentName} />
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default AgentInboxChannelContainer;
